// The in-room runtime. Runs inside the sealed plugin worker: it builds the
// `slate` API on top of an RpcPeer (every method is a call across the counter),
// exposes the plugin's registered handlers so the host can invoke them, and
// drives activate / deactivate. No DOM, no network, no host access: the channel
// is the only way in or out.

import { RpcPeer, type RpcChannel } from '../rpc/peer';
import { HOST_METHODS, WORKER_METHODS, EVENTS } from '../rpc/methods';
import type { PermissionCapability } from '../manifest';
import type {
  ActivationContext,
  Disposable,
  FoldProvider,
  FoldRange,
  PermissionState,
  PluginModule,
  QuickPickItem,
  SlateApi,
  SurfaceHandle,
  TreeDataProvider,
  TreeItem,
} from '../api/types';

export interface PluginRuntime {
  readonly slate: SlateApi;
  activate(): Promise<void>;
  deactivate(): Promise<void>;
  /** Full teardown: deactivate and close the RPC channel. */
  dispose(): void;
}

export function startPlugin(channel: RpcChannel, module: PluginModule): PluginRuntime {
  const peer = new RpcPeer(channel);
  const commands = new Map<string, (...args: unknown[]) => unknown>();
  const trees = new Map<string, TreeDataProvider>();
  const folds = new Map<string, FoldProvider>();
  const subscriptions: Disposable[] = [];

  // Fire-and-forget host notifications; swallow rejections so an unhandled host
  // method never crashes the worker.
  const notify = (method: string, params: unknown): void => {
    void peer.call(method, params).catch(() => undefined);
  };

  const surfaces = new Map<string, Set<(message: unknown) => void>>();
  const surfaceSubs = (key: string): Set<(message: unknown) => void> => {
    let s = surfaces.get(key);
    if (!s) {
      s = new Set();
      surfaces.set(key, s);
    }
    return s;
  };
  const makeSurfaceHandle = (
    key: string,
    registerMethod: string,
    unregisterMethod: string,
    idParam: 'surfaceId' | 'viewId',
  ): SurfaceHandle => {
    const subs = surfaceSubs(key);
    notify(registerMethod, { [idParam]: key });
    return {
      postMessage: (message) => notify(HOST_METHODS.surfacePost, { surfaceId: key, message }),
      onMessage: (cb) => {
        subs.add(cb);
        return { dispose: () => void subs.delete(cb) };
      },
      dispose: () => {
        surfaces.delete(key);
        notify(unregisterMethod, { [idParam]: key });
      },
    };
  };

  // Handlers the HOST invokes on this worker.
  peer.handle(WORKER_METHODS.commandInvoke, async (params) => {
    const { id, args } = params as { id: string; args?: unknown[] };
    const handler = commands.get(id);
    if (!handler) throw new Error(`no command "${id}"`);
    return await handler(...(args ?? []));
  });
  peer.handle(WORKER_METHODS.viewGetChildren, async (params) => {
    const { viewId, parentId } = params as { viewId: string; parentId?: string };
    const provider = trees.get(viewId);
    if (!provider) throw new Error(`no view "${viewId}"`);
    return (await provider.getChildren(parentId)) satisfies TreeItem[];
  });
  peer.handle(WORKER_METHODS.surfaceDeliver, (params) => {
    const { surfaceId, message } = params as { surfaceId: string; message: unknown };
    const subs = surfaces.get(surfaceId);
    if (subs) for (const cb of [...subs]) cb(message);
  });
  peer.handle(WORKER_METHODS.editorGetFolds, async (params) => {
    const { providerId, source, fileName } = params as {
      providerId: string;
      source: string;
      fileName: string;
    };
    const provider = folds.get(providerId);
    if (!provider) throw new Error(`no fold provider "${providerId}"`);
    return (await provider.getFolds(source, fileName)) satisfies FoldRange[];
  });

  const slate: SlateApi = {
    commands: {
      register(id, handler) {
        commands.set(id, handler);
        notify(HOST_METHODS.commandsRegister, { id });
        return {
          dispose: () => {
            commands.delete(id);
            notify(HOST_METHODS.commandsUnregister, { id });
          },
        };
      },
      executeCommand(id, ...args) {
        return peer.call(HOST_METHODS.commandsExecute, { id, args });
      },
    },
    window: {
      showMessage(message, options) {
        return peer.call(HOST_METHODS.windowShowMessage, { message, options }) as Promise<
          string | undefined
        >;
      },
      showQuickPick(items, options) {
        return peer.call(HOST_METHODS.windowShowQuickPick, { items, options }) as Promise<
          QuickPickItem | undefined
        >;
      },
      createSurface(id) {
        return makeSurfaceHandle(
          id,
          HOST_METHODS.surfaceRegister,
          HOST_METHODS.surfaceUnregister,
          'surfaceId',
        );
      },
      showModal(surfaceId) {
        return peer.call(HOST_METHODS.windowShowModal, { surfaceId }) as Promise<void>;
      },
    },
    views: {
      registerTreeDataProvider(viewId, provider) {
        trees.set(viewId, provider);
        notify(HOST_METHODS.viewsRegister, { viewId });
        return {
          dispose: () => {
            trees.delete(viewId);
            notify(HOST_METHODS.viewsUnregister, { viewId });
          },
        };
      },
      registerSurfaceView(viewId) {
        return makeSurfaceHandle(
          viewId,
          HOST_METHODS.viewsRegisterSurface,
          HOST_METHODS.viewsUnregisterSurface,
          'viewId',
        );
      },
    },
    permissions: {
      query(capability) {
        return peer.call(HOST_METHODS.permissionsQuery, { capability }) as Promise<PermissionState>;
      },
      request(capability) {
        return peer.call(HOST_METHODS.permissionsRequest, { capability }) as Promise<boolean>;
      },
      onDidChange(cb) {
        const off = peer.on(EVENTS.permissionsChanged, (payload) => {
          const { capability, state } = payload as {
            capability: PermissionCapability;
            state: PermissionState;
          };
          cb(capability, state);
        });
        return { dispose: off };
      },
    },
    fs: {
      read(path) {
        return peer.call(HOST_METHODS.fsRead, { path }) as Promise<string>;
      },
    },
    editor: {
      getActiveText() {
        return peer.call(HOST_METHODS.editorGetActiveText, {}) as Promise<string | undefined>;
      },
      registerFoldProvider(provider) {
        folds.set(provider.id, provider);
        notify(HOST_METHODS.editorRegisterFoldProvider, {
          providerId: provider.id,
          extensions: provider.extensions,
        });
        return {
          dispose: () => {
            folds.delete(provider.id);
            notify(HOST_METHODS.editorUnregisterFoldProvider, { providerId: provider.id });
          },
        };
      },
    },
  };

  const context: ActivationContext = { subscriptions, slate };

  const runtime: PluginRuntime = {
    slate,
    async activate() {
      await module.activate?.(context);
    },
    async deactivate() {
      try {
        await module.deactivate?.();
      } finally {
        for (const d of subscriptions.splice(0)) {
          try {
            d.dispose();
          } catch {
            /* a faulty disposer must not block teardown */
          }
        }
      }
    },
    dispose() {
      void runtime.deactivate();
      peer.dispose();
    },
  };

  // The host drives the lifecycle over the same channel, so the trusted
  // (in-process) and sealed (worker) tiers behave identically.
  peer.handle(WORKER_METHODS.lifecycleActivate, () => runtime.activate());
  peer.handle(WORKER_METHODS.lifecycleDeactivate, () => runtime.deactivate());

  return runtime;
}
